/**
 * ItemShipPicker — line-item picker for partial fulfillment.
 *
 * Each row: checkbox + thumbnail + item name + ordered/shipped qty + qty stepper.
 * Manages selections as a Map<so_detail, qty_to_ship>.
 */

import { Package } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { QtyStepperInput } from "./QtyStepperInput";
import type { SalesOrderItem } from "@/lib/erp/types/order";

/** SO Item with remaining qty > 0 */
export interface ShipItem {
  name: string;
  item_code: string;
  item_name: string;
  qty: number;
  delivered_qty: number;
  image?: string;
  rate: number;
}

interface ItemShipPickerProps {
  items: ShipItem[];
  selections: Map<string, number>;
  onSelectionsChange: (selections: Map<string, number>) => void;
  disabled?: boolean;
}

/** Convert SalesOrderItem[] to ShipItem[], filtering to items with remaining qty. */
export function toShipItems(items: SalesOrderItem[]): ShipItem[] {
  return items
    .filter((item) => {
      const remaining = item.qty - (item.delivered_qty || 0);
      return remaining > 0;
    })
    .map((item) => ({
      name: item.name,
      item_code: item.item_code,
      item_name: item.item_name,
      qty: item.qty,
      delivered_qty: item.delivered_qty || 0,
      image: item.image,
      rate: item.rate,
    }));
}

export function ItemShipPicker({
  items,
  selections,
  onSelectionsChange,
  disabled = false,
}: ItemShipPickerProps) {
  const handleToggle = (soDetail: string, remaining: number) => {
    const next = new Map(selections);
    if (next.has(soDetail)) {
      next.delete(soDetail);
    } else {
      next.set(soDetail, remaining);
    }
    onSelectionsChange(next);
  };

  const handleQtyChange = (soDetail: string, qty: number) => {
    const next = new Map(selections);
    next.set(soDetail, qty);
    onSelectionsChange(next);
  };

  if (items.length === 0) {
    return (
      <p className="py-2 text-sm text-muted-foreground">
        ไม่มีสินค้าที่ต้องจัดส่ง
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {items.map((item) => {
        const remaining = item.qty - item.delivered_qty;
        const isSelected = selections.has(item.name);
        const qtyToShip = selections.get(item.name) ?? remaining;

        return (
          <div
            key={item.name}
            role="button"
            tabIndex={0}
            onClick={(e) => {
              // Don't toggle when clicking the qty stepper
              if ((e.target as HTMLElement).closest("[data-qty-stepper]")) return;
              if (!disabled) handleToggle(item.name, remaining);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (!disabled) handleToggle(item.name, remaining);
              }
            }}
            className={`flex items-center gap-3 rounded-xl border p-3 transition-colors cursor-pointer select-none ${
              isSelected
                ? "border-primary/40 bg-primary/[0.03]"
                : "border-border hover:border-muted-foreground/30 hover:bg-muted/30"
            } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => handleToggle(item.name, remaining)}
              disabled={disabled}
              aria-label={`เลือก ${item.item_name}`}
              className="shrink-0 pointer-events-none"
            />

            {/* Thumbnail */}
            <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-muted flex items-center justify-center">
              {item.image ? (
                <img
                  src={item.image}
                  alt={item.item_name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <Package className="h-4 w-4 text-muted-foreground" />
              )}
            </div>

            {/* Item info */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{item.item_name}</p>
              <p className="text-xs text-muted-foreground">
                สั่ง {item.qty} ชิ้น
                {item.delivered_qty > 0 && (
                  <> &middot; ส่งแล้ว {item.delivered_qty}</>
                )}
              </p>
            </div>

            {/* Qty stepper — only shown when checked */}
            {isSelected && (
              <div data-qty-stepper>
                <QtyStepperInput
                  value={qtyToShip}
                  onChange={(v) => handleQtyChange(item.name, v)}
                  min={1}
                  max={remaining}
                  disabled={disabled}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
